import i18n from '@/utils/i18n';
import React from 'react';
import {
  AsYouType,
  CountryCode,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import { buildProspectDetailsUpdate } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-edit-panel/blocs/transformers/prospect-details-update-transformer';
import {
  Button,
  IconButton,
  Icons,
  ScrollView,
  SidePanel,
  Typography,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_COUNTRY_CODE } from '@/utils/phone-number';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { getCountryCurrencyCode, getCurrencySymbol } from '@/utils/money';
import {
  PRICE_INPUT_REGEX,
  stripCommas,
} from '@/domain/pricing/utils/pricing-utils';
import { ProspectEditForm } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-edit-panel/components/prospect-edit-form';
import { ProspectEditFormSkeleton } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-edit-panel/components/prospect-edit-form-skeleton';
import { useEditableProspectLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/loaders/use-editable-prospect-loader';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useProspectEditForm } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-edit-panel/blocs/hooks/use-prospect-edit-form';
import { useSalesPortalSupplierMetadataLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-supplier-metadata-loader';
import { useUpdateProspectDetailsMutator } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/mutators/use-update-prospect-details-mutator';

interface ProspectEditPanelContentProps {
  onClose: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export const ProspectEditPanelContent: React.FC<
  ProspectEditPanelContentProps
> = ({ onClose, restaurantUUID, supplierUUID }) => {
  const [styles, theme] = useStyles();
  const { showErrorToast, showToast } = useExperienceToasts();

  const { countryCode: supplierCountryCode } =
    useSalesPortalSupplierMetadataLoader({ supplierUUID });
  const phoneCountryCode = (
    supplierCountryCode || DEFAULT_COUNTRY_CODE
  ).toUpperCase() as CountryCode;

  const {
    errors,
    hasValidationErrors,
    editedFields,
    hydrate,
    isDirty,
    setFieldTouched,
    setFormField,
    values,
  } = useProspectEditForm({ phoneCountryCode });

  const currencySymbol = getCurrencySymbol(
    getCountryCurrencyCode(supplierCountryCode),
  );

  const {
    error: prospectError,
    loading: prospectLoading,
    refetch: refetchProspectDetails,
  } = useEditableProspectLoader({
    onCompleted: hydrate,
    restaurantUUID,
    supplierUUID,
  });

  const { loading: saving, updateProspectDetails } =
    useUpdateProspectDetailsMutator({
      onError: () => {
        showErrorToast(i18n.t('sales_portal.edit_prospect.error'));
      },
      onSuccess: () => {
        showToast({
          title: i18n.t('sales_portal.edit_prospect.success'),
          variant: 'success',
        });
        onClose();
      },
    });

  const handlePhoneChange = (text: string) => {
    if (text.length < values.contactPhone.length) {
      setFormField('contactPhone', text);
      return;
    }
    if (validatePhoneNumberLength(text, phoneCountryCode) === 'TOO_LONG') {
      return;
    }
    setFormField('contactPhone', new AsYouType(phoneCountryCode).input(text));
  };

  const handleWeeklyValueChange = (text: string) => {
    if (text && !PRICE_INPUT_REGEX.test(text)) {
      return;
    }
    setFormField('weeklyValue', stripCommas(text));
  };

  const handleSave = async () => {
    if (hasValidationErrors) {
      return;
    }
    const prospectDetails = buildProspectDetailsUpdate({
      editedFields,
      phoneCountryCode,
      values,
    });
    await updateProspectDetails({
      prospectDetails,
      restaurantUUID,
      supplierUUID,
    });
  };

  const saveDisabled =
    hasValidationErrors || saving || prospectLoading || !isDirty;

  return (
    <>
      <SidePanel.Header
        leadingActions={
          <IconButton
            aria-label={i18n.t('sales_portal.side_panel.close')}
            icon={<Icons.X color={theme.sk.color.icon.primary} />}
            onPress={onClose}
            shape="rounded"
            size="$sm"
            variant="tertiary"
          />
        }
      />
      <SidePanel.Body
        accessibilityLabel={i18n.t('sales_portal.edit_prospect.title')}
        gap={theme.sk.spacing.spaceLarge}
        paddingTop="$0"
        role="dialog"
      >
        <Typography accessibilityRole="header" size="$lg" variant="heading">
          {i18n.t('sales_portal.edit_prospect.title')}
        </Typography>
        {prospectError ? (
          <ErrorState
            buttonLabel={i18n.t('sales_portal.error.refresh')}
            description={i18n.t(
              'sales_portal.edit_prospect.load_error.description',
            )}
            onPress={refetchProspectDetails}
            title={i18n.t('sales_portal.edit_prospect.load_error.title')}
          />
        ) : (
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator
          >
            {prospectLoading ? (
              <ProspectEditFormSkeleton />
            ) : (
              <ProspectEditForm
                currencySymbol={currencySymbol}
                errors={errors}
                onFieldBlur={setFieldTouched}
                onFieldChange={setFormField}
                onPhoneChange={handlePhoneChange}
                onWeeklyValueChange={handleWeeklyValueChange}
                values={values}
              />
            )}
          </ScrollView>
        )}
      </SidePanel.Body>
      <SidePanel.Footer>
        <XStack
          gap={theme.sk.spacing.spaceSmall}
          justifyContent="flex-end"
          width="100%"
        >
          <Button
            accessibilityLabel={i18n.t('cancel')}
            onPress={onClose}
            size="$md"
            variant="secondary"
          >
            {i18n.t('cancel')}
          </Button>
          <Button
            accessibilityLabel={i18n.t('sales_portal.edit_prospect.save')}
            disabled={saveDisabled}
            loading={saving}
            onPress={handleSave}
            size="$md"
            variant="primary"
          >
            {i18n.t('sales_portal.edit_prospect.save')}
          </Button>
        </XStack>
      </SidePanel.Footer>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  scrollContent: {
    gap: theme.sk.spacing.spaceMedium,
  },
}));
